import { useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { EquityCurveResponse } from '@pnl/shared';
import { useChartColors } from '../../lib/chartColors';
import { formatBrl, formatDateShort } from '../../lib/format';

interface Props {
  data: EquityCurveResponse;
}

// Estilo FTMO: teal (verde original) acima da linha do tamanho da conta, vermelho abaixo.
const GREEN = '#10D9C4';
const RED = '#E53E3E';

interface ChartRow {
  date: string;
  label: string;
  balance: number;
  equity: number;
}

interface TooltipPayload {
  payload: ChartRow;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  zeroPoint?: number;
}

function CustomTooltip({ active, payload, zeroPoint = 0 }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]!.payload;
  const color = row.balance >= zeroPoint ? GREEN : RED;
  return (
    <div className="border-ink-700 bg-ink-900 rounded-md border px-3 py-2 text-xs shadow-lg">
      <p className="text-ink-300 font-semibold">{row.label}</p>
      <p className="mt-1 tabular-nums" style={{ color }}>
        Balance: {formatBrl(row.balance * 100)}
      </p>
      <p className="text-ink-100 tabular-nums">Equidade: {formatBrl(row.equity * 100)}</p>
    </div>
  );
}

// Label "Tamanho da Conta" — caixa branca com texto preto, no canto esquerdo da baseline.
interface BaselineLabelProps {
  viewBox?: { x?: number; y?: number };
  value?: string;
}
function BaselineLabel({ viewBox, value = '' }: BaselineLabelProps) {
  const x = viewBox?.x ?? 0;
  const y = viewBox?.y ?? 0;
  const w = value.length * 5.4 + 14;
  return (
    <g transform={`translate(${x + 4}, ${y - 9})`}>
      <rect width={w} height={18} rx={3} fill="#FFFFFF" />
      <text x={7} y={13} fontSize={10} fontWeight={700} fill="#0A0A0A">
        {value}
      </text>
    </g>
  );
}

export function EquityCurveChart({ data }: Props) {
  const [showObjectives, setShowObjectives] = useState(true);
  const chart = useChartColors();

  const rows: ChartRow[] = useMemo(
    () =>
      data.points.map((p) => ({
        date: p.date,
        label: formatDateShort(p.date),
        balance: p.balanceCents / 100,
        equity: p.equityCents / 100,
      })),
    [data.points],
  );

  const zeroPointValue = data.zeroPointCents / 100;
  const latest = rows[rows.length - 1] ?? { balance: 0, equity: 0 };
  const latestPositive = latest.balance >= zeroPointValue;

  // Offset (0=topo, 1=base) onde a linha do tamanho da conta cruza a área.
  // IMPORTANTE: o <linearGradient> usa objectBoundingBox — mapeia o offset ao
  // bounding box REAL da área (de dataMin a dataMax), NÃO ao domínio com padding
  // do YAxis. Por isso o offset tem que usar o range CRU dos dados; com padding
  // o corte verde/vermelho ficava acima da base e pintava vermelho fantasma.
  const balances = rows.map((r) => r.balance);
  const dataMax = balances.length ? Math.max(...balances) : zeroPointValue;
  const dataMin = balances.length ? Math.min(...balances) : zeroPointValue;
  const gradientOffset =
    dataMax <= zeroPointValue
      ? 0
      : dataMin >= zeroPointValue
        ? 1
        : (dataMax - zeroPointValue) / (dataMax - dataMin);

  // Domínio do eixo Y SEMPRE inclui o tamanho da conta. Antes usava só
  // dataMin/dataMax: numa conta que nunca chegou perto do ponto zero (ex.: a
  // 150k, que abriu em 152k e só subiu), a linha "Tamanho da Conta" caía FORA
  // da área visível e simplesmente não aparecia. Com o piso/teto ancorados no
  // ponto zero, o marcador fica sempre à vista — com o gráfico verde por cima
  // dele ou vermelho por baixo, conforme o resultado.
  const pad = Math.max(1000, (dataMax - dataMin) * 0.08);
  const yDomain: [number, number] = [
    Math.min(dataMin, zeroPointValue) - pad,
    Math.max(dataMax, zeroPointValue) + pad,
  ];

  return (
    <div className="border-ink-800 bg-ink-900 relative rounded-xl border p-5">
      {/* Labels flutuantes topo esquerdo */}
      <div className="absolute left-5 top-5 z-10 flex flex-col gap-1">
        <div
          className="inline-flex items-center gap-2 rounded-md px-2.5 py-1 text-xs font-semibold tabular-nums"
          style={{
            background: latestPositive ? 'rgba(16,217,196,0.15)' : 'rgba(229,62,62,0.15)',
            color: latestPositive ? GREEN : RED,
          }}
        >
          {formatBrl(latest.balance * 100)} <span className="text-2xs opacity-80">Balance</span>
        </div>
        <div className="bg-warning-500 inline-flex items-center gap-2 rounded-md px-2.5 py-1 text-xs font-semibold tabular-nums text-black">
          {formatBrl(latest.equity * 100)} <span className="text-2xs opacity-70">Equidade</span>
        </div>
      </div>

      {/* Controles topo direito */}
      <div className="absolute right-5 top-5 z-10 flex items-center gap-2">
        <div className="bg-ink-800 border-ink-700 inline-flex rounded-md border text-xs">
          <button
            type="button"
            onClick={() => setShowObjectives(true)}
            className={
              'rounded-l-md px-2.5 py-1 font-medium transition-colors ' +
              (showObjectives ? 'bg-brand-500 text-white' : 'text-ink-300 hover:text-ink-100')
            }
          >
            Ativo
          </button>
          <button
            type="button"
            onClick={() => setShowObjectives(false)}
            className={
              'rounded-r-md px-2.5 py-1 font-medium transition-colors ' +
              (!showObjectives ? 'bg-brand-500 text-white' : 'text-ink-300 hover:text-ink-100')
            }
          >
            Desativado
          </button>
        </div>
      </div>

      <div className="mt-20 h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
            <defs>
              {/* Split verde/vermelho na linha do tamanho da conta */}
              <linearGradient id="equity-stroke" x1="0" y1="0" x2="0" y2="1">
                <stop offset={0} stopColor={GREEN} />
                <stop offset={gradientOffset} stopColor={GREEN} />
                <stop offset={gradientOffset} stopColor={RED} />
                <stop offset={1} stopColor={RED} />
              </linearGradient>
              <linearGradient id="equity-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset={0} stopColor={GREEN} stopOpacity={0.35} />
                <stop offset={gradientOffset} stopColor={GREEN} stopOpacity={0.04} />
                <stop offset={gradientOffset} stopColor={RED} stopOpacity={0.04} />
                <stop offset={1} stopColor={RED} stopOpacity={0.35} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={chart.grid} strokeDasharray="3 3" vertical={false} />
            {/* Linhas finas dos eixos (esquerda subindo + base), como na refer\u00EAncia */}
            <XAxis
              dataKey="label"
              tick={{ fill: chart.tick, fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: chart.axis, strokeWidth: 1 }}
            />
            <YAxis
              tick={{ fill: chart.tick, fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: chart.axis, strokeWidth: 1 }}
              tickFormatter={(v: number) =>
                `R$\u00A0${v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`
              }
              domain={yDomain}
              // Mais marcas: a r\u00E9gua da esquerda passa a refletir melhor os
              // patamares que o saldo realmente atingiu no hist\u00F3rico.
              tickCount={8}
              width={90}
            />
            <Tooltip
              content={<CustomTooltip zeroPoint={zeroPointValue} />}
              cursor={{ stroke: '#1E6FFF', strokeDasharray: '3 3' }}
            />
            <Area
              // MEIO-TERMO (pedido do cliente): `monotone` arredonda de leve as
              // viradas, mas — diferente de splines como `natural`/`basis` — NÃO
              // ultrapassa os valores reais, então nunca desenha um saldo que o
              // trader não teve. Com os dias parados preenchidos (getEquityCurve),
              // a linha tem mais pontos e o arredondamento fica sutil: variação
              // forte continua virando quase em bico, dia parado segue reto.
              type="monotone"
              dataKey="balance"
              stroke="url(#equity-stroke)"
              strokeWidth={2.5}
              fill="url(#equity-fill)"
              dot={false}
              activeDot={{ r: 4, fill: '#1E6FFF', stroke: '#ffffff', strokeWidth: 1.5 }}
              // Ancora o preenchimento no nível base da conta: acima da base pinta
              // verde, e só pinta vermelho onde a linha realmente cai abaixo da base.
              // Sem isso, a área descia até o fundo do gráfico e mostrava vermelho
              // mesmo com o saldo sempre acima da base.
              baseValue={zeroPointValue}
              isAnimationActive={true}
              animationDuration={500}
            />
            {showObjectives && (
              <ReferenceLine
                y={zeroPointValue}
                stroke={chart.baseline}
                strokeDasharray="4 4"
                strokeWidth={1}
                label={
                  <BaselineLabel value={`Tamanho da Conta · ${formatBrl(data.zeroPointCents)}`} />
                }
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <p className="text-2xs text-ink-500 mt-3 flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className="bg-danger-500 absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" />
          <span className="bg-danger-500 relative inline-flex h-2.5 w-2.5 rounded-full shadow-[0_0_8px_rgba(232,64,64,0.8)]" />
        </span>
        As posições atuais serão atualizadas após auditoria diária
      </p>
    </div>
  );
}
